import {event_getEventsByProps_Api, event_getUpcomingEventsByProps_Api} from 'apis/event'
import {getId, getType} from 'apis/model/base'
import _ from 'lodash'
import React, {useContext} from 'react'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import OrganizationContainerContext from '../OrganizationContainerContext'
import useTranslate from '../../../../modules/local/useTranslate'
import {WrapperBlockSider} from "../components/WrapperBlockSider";
import renderPagingLoading from "../../../MainPage/functions/renderPagingLoading";
import CustomLazyPaginationWrapper from "../../../../modules/asyncCache/components/CustomLazyPaginationWrapper";
import {getEventTypeTitle} from "../../../Event/components/EventOverviewSetting";
import getTitle from "../../../../helpers/getTitle";
import {Link} from "react-router-dom";
import getLinkToDetail from "../../../../helpers/getLinkToDetail";
import {formatSimplifyDate} from "../../../../helpers/dateUtils";
import {scrollToRow} from "../../Missal/functions/scrollToRow";
import {FiChevronsRight} from "react-icons/fi";
import {useHistory} from "../../../../modules/navigation/useRouter";
import {BsFillCalendar2DateFill, BsPinMapFill} from "react-icons/bs";
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";
import dayjs from "dayjs";
import classNames from "classnames";
import {HeaderWithIconLine} from "../../Templates/items/ListHeader";
import EmptyHolder from "../../../../components/EmptyHolder";
import {Skeleton} from "antd";
import {ItemInfoWithIcon} from "../../../../components/common/ItemInfoWithIcon";

// ─── Status badge ─────────────────────────────────────────────────────────────

const EventStatusBadge = ({time_open, time_close, is_expired}) => {
    const t = useTranslate()
    const now = dayjs()
    const open = time_open ? dayjs.utc(time_open).local() : null
    const close = time_close ? dayjs.utc(time_close).local() : null

    if (is_expired || (close && now.isAfter(close))) {
        return (
            <span className="px-2 py-0.5 text-xs font-semibold text-color-300 bg-color-50 rounded-full border border-color-100">
                {t('closed')}
            </span>
        )
    }
    if (open && now.isBefore(open)) {
        return (
            <span className="px-2 py-0.5 text-xs font-semibold text-green-700 bg-green-50 rounded-full border border-green-200">
                {t('upcoming')}
            </span>
        )
    }
    return (
        <span className="px-2 py-0.5 text-xs font-semibold text-blue-700 bg-blue-50 rounded-full border border-blue-200">
            {t('ongoing')}
        </span>
    )
}

// ─── Date block ───────────────────────────────────────────────────────────────

const EventDateBlock = ({time_open, time_close, is_expired}) => {
    if (!time_open) return null
    const d = dayjs.utc(time_open).local()
    const now = dayjs()
    const close = time_close ? dayjs.utc(time_close).local() : null
    const isPast = is_expired || (close && now.isAfter(close))
    const isNow = !isPast && close && now.isAfter(d)

    const accent = isPast
        ? 'border-color-100'
        : isNow
            ? 'border-blue-400'
            : 'border-green-400'

    return (
        <div className={classNames('flex-shrink-0 flex flex-col items-center px-3 py-2 border-l-4 rounded-l-xl', accent, 'background-50')}>
            <span className="text-xs font-semibold text-color-300 uppercase tracking-wider">
                {d.format('MMM')}
            </span>
            <span className={classNames('text-3xl font-bold leading-none', isPast ? 'text-color-300' : 'text-primary')}>
                {d.format('DD')}
            </span>
            <span className="text-xs text-color-400">{d.format('YYYY')}</span>
        </div>
    )
}

// ─── Event card ───────────────────────────────────────────────────────────────

const EventCard = ({item, t}) => {
    const {title, time_open, time_close, location_name, event_type, is_expired} = item ?? {}
    const typeTitle = getEventTypeTitle(event_type)

    return (
        <div className="flex background border border-color-50 rounded-xl shadow-sm hover:shadow-md hover:scale-2xs transition-all overflow-hidden">
            <EventDateBlock time_open={time_open} time_close={time_close} is_expired={is_expired}/>
            <div className="flex flex-col flex-1 min-w-0 px-4 py-3 gap-1">
                <div className="flex items-start justify-between gap-2">
                    <Link
                        to={getLinkToDetail(item)}
                        className="font-bold text-base text-primary leading-snug no-underline hover:underline max-lines-2 flex-1 min-w-0">
                        {getTitle(item) || title}
                    </Link>
                    <div className="flex-shrink-0 mt-0.5">
                        <EventStatusBadge time_open={time_open} time_close={time_close} is_expired={is_expired}/>
                    </div>
                </div>
                {typeTitle && (
                    <span className="text-xs text-indigo-500 italic font-medium tracking-wide">
                        {t(typeTitle)}
                    </span>
                )}
                {time_close && (
                    <div className="flex items-center gap-1 text-xs text-color-300">
                        <BsFillCalendar2DateFill size={11} className="text-green-500 flex-shrink-0"/>
                        <span>{t('to date')}: {formatSimplifyDate(time_close, 'medium')}</span>
                    </div>
                )}
                {location_name && (
                    <div className="flex items-center gap-1 text-xs text-color-300">
                        <BsPinMapFill size={11} className="text-red-400 flex-shrink-0"/>
                        <span className="max-lines-1">{location_name}</span>
                    </div>
                )}
            </div>
        </div>
    )
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

const EventSkeleton = () => (
    <div className="flex flex-col gap-4">
        {[1, 2, 3].map(i => (
            <div key={i} className="flex background border border-color-50 rounded-xl overflow-hidden">
                <div className="w-16 flex-shrink-0 background-100"/>
                <div className="flex-1 px-4 py-3">
                    <Skeleton active title={{width: '60%'}} paragraph={{rows: 2}}/>
                </div>
            </div>
        ))}
    </div>
)

// ─── Sidebar upcoming events ──────────────────────────────────────────────────

export const OrganizationUpcomingEventBlock = ({}) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const history = useHistory()
    const {organization: item} = useContext(OrganizationContainerContext)
    const [id, type] = [getId(item), getType(item)]

    if (_.isEmpty(item)) return null

    return (
        <CustomLazyPaginationWrapper
            sample={2}
            apiInfo={event_getUpcomingEventsByProps_Api}
            renderLoading={renderPagingLoading}
            query={bindQueryParams([{id}, {type}])}
            renderWrap={(children, __, count) => {
                return !!count && (
                    <WrapperBlockSider
                        title="upcoming schedule" titleColor="text-green-700"
                        borderLeft={isLg}
                        borderTop={!isLg}
                        icon={<BsFillCalendar2DateFill size={18} className="text-green-500"/>}>
                        <div className="flex flex-col gap-4 w-full">
                            {children}
                        </div>
                    </WrapperBlockSider>
                )
            }}
            renderItem={(e, i) => {
                const getStatus = (time_open, time_close) => {
                    const now = dayjs()
                    const open = dayjs.utc(time_open).local()
                    const close = dayjs.utc(time_close).local()
                    if (now.isBefore(open)) return <span className="font-medium text-green-600 italic">{t('upcoming')}</span>
                    if (now.isAfter(close)) return <span className="font-medium text-primary-500 italic">{t('closed')}</span>
                    return <span className="font-medium text-blue-600 italic">{t('ongoing')}</span>
                }
                return (
                    <div key={i} className="px-3 py-2 background-50 hover:scale-2xs hover:shadow-md rounded-xl">
                        <div className="mb-2 pb-2 border-b flex flex-col">
                            <Link to={getLinkToDetail(e)}
                                  className="font-bold text-base text-color-000 leading-snug max-lines-1 no-underline">
                                {getTitle(e)}
                            </Link>
                            <span className="text-xs text-indigo-400 italic leading-snug tracking-wider">
                                {t(getEventTypeTitle(e?.event_type))}
                            </span>
                        </div>
                        <div className="space-x-1">
                            <BsFillCalendar2DateFill size={12} className="mx-1 text-green-500"/>
                            <span className="text-xs text-gray-700 tracking-wide">
                                {formatSimplifyDate(e?.time_open, 'fullTime')}
                                {' - '}{getStatus(e?.time_open, e?.time_close)}
                            </span>
                        </div>
                        <ItemInfoWithIcon
                            icon={<BsPinMapFill size={12} className="mx-1 text-green-500"/>}
                            value={e?.location_name}
                            className="text-xs italic tracking-wide"
                        />
                    </div>
                )
            }}
            renderLoadMore={() => (
                <span
                    className="flex items-center justify-end gap-1 text-color-400 hover:text-color-100 cursor-pointer"
                    onClick={() => {
                        scrollToRow('contentOrg')
                        history.push(getLinkToDetail(item) + '/events')
                    }}>
                    <span className="text-xs italic">{t('see more')}</span>
                    <FiChevronsRight size={13} className="my-auto"/>
                </span>
            )}
        />
    )
}

// ─── Main event list ──────────────────────────────────────────────────────────

const OrganizationEventList = ({Wrapper = 'div'}) => {
    const t = useTranslate()
    const {organization: item} = useContext(OrganizationContainerContext)
    const [id, type] = [getId(item), getType(item)]

    if (_.isEmpty(item)) return null

    return (
        <Wrapper className="flex flex-col gap-6 pb-8">
            <HeaderWithIconLine
                icon={<BsFillCalendar2DateFill size={20} className="text-green-500"/>}
                renderTitle={() => (
                    <div className="flex flex-col gap-0.5">
                        <span className="font-bold text-base text-color-000 uppercase tracking-wide">
                            {t('event')}
                        </span>
                    </div>
                )}
                renderSubTitle={() => (
                    <div className="flex flex-col gap-0.5">

                        <span className="text-xs text-color-300 italic font-normal normal-case tracking-normal">
                            {t('event introduction description')}
                        </span>
                    </div>
                )}
            />
            <CustomLazyPaginationWrapper
                alwaysVisible={true}
                apiInfo={event_getEventsByProps_Api}
                query={bindQueryParams([{id}, {type}])}
                renderItem={(e) => <EventCard key={getId(e)} item={e} t={t}/>}
                renderLoading={() => <EventSkeleton/>}
                renderWrap={(children, __, count) => {
                    if (!count) return (
                        <EmptyHolder
                            title={t('event')}
                            subTitle={t('No data available')}
                        />
                    )
                    return (
                        <div className="flex flex-col gap-4">
                            {children}
                        </div>
                    )
                }}
            />
        </Wrapper>
    )
}

export default OrganizationEventList
